import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { apiService } from "../api/services/apiService";
import { useAuth } from "../contexts/AuthContext";
import { EntityStatus } from "../types";
import { resolveTenantId } from "../utils/jwtUtils";
import {
  filterSpecFiles,
  getApiNameFromFileName,
  readSpecContent,
} from "../utils/apiSpecBulkUpload";

const DEFAULT_BULK_VERSION = "1.0.0";

type DialogStep = "confirmSubdirs" | "uploading" | "summary";

interface BulkUploadApiSpecsDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: (hadSuccess: boolean) => void;
}

interface UploadFailure {
  path: string;
  error: string;
}

const BulkUploadApiSpecsDialog: React.FC<BulkUploadApiSpecsDialogProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef(false);
  const includeSubdirsRef = useRef<boolean | null>(null);

  const [step, setStep] = useState<DialogStep>("confirmSubdirs");
  const [includeSubdirs, setIncludeSubdirs] = useState<boolean | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const [totalCount, setTotalCount] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);

  const [successes, setSuccesses] = useState<string[]>([]);
  const [failures, setFailures] = useState<UploadFailure[]>([]);

  const [version, setVersion] = useState(DEFAULT_BULK_VERSION);
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.DRAFT);
  const [enabled, setEnabled] = useState(false);

  const versionValid = version.trim().length > 0;

  const resetState = () => {
    abortRef.current = false;
    setStep("confirmSubdirs");
    setIncludeSubdirs(null);
    includeSubdirsRef.current = null;
    setScanError(null);
    setTotalCount(0);
    setUploadedCount(0);
    setSuccesses([]);
    setFailures([]);
    setVersion(DEFAULT_BULK_VERSION);
    setStatus(EntityStatus.DRAFT);
    setEnabled(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleEnabledChange = (checked: boolean) => {
    setEnabled(checked);
    setStatus(checked ? EntityStatus.ACTIVE : EntityStatus.INACTIVE);
  };

  const handleStatusChange = (newStatus: EntityStatus) => {
    setStatus(newStatus);
    setEnabled(newStatus === EntityStatus.ACTIVE);
  };

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open]);

  const handleDialogClose = () => {
    if (step === "summary") {
      const hadSuccess = successes.length > 0;
      resetState();
      onClose();
      if (hadSuccess) {
        onComplete(true);
      }
      return;
    }
    if (step !== "uploading") {
      resetState();
      onClose();
    }
  };

  const handleSubdirChoice = (include: boolean) => {
    if (!versionValid) return;
    includeSubdirsRef.current = include;
    setIncludeSubdirs(include);
    setScanError(null);
    fileInputRef.current?.click();
  };

  const runUpload = async (specFiles: File[]) => {
    const tenantId = resolveTenantId(user?.tenantId);
    if (!tenantId) {
      setScanError(
        "Tenant ID not found. Ensure you are signed in and your session includes a tenant.",
      );
      return;
    }

    setStep("uploading");
    setTotalCount(specFiles.length);
    setUploadedCount(0);
    setSuccesses([]);
    setFailures([]);
    abortRef.current = false;

    const successPaths: string[] = [];
    const failureEntries: UploadFailure[] = [];

    for (const file of specFiles) {
      if (abortRef.current) break;

      const path = file.webkitRelativePath || file.name;
      try {
        const specContent = await readSpecContent(file);
        await apiService.createApi({
          tenantId,
          name: getApiNameFromFileName(file.name),
          version: version.trim(),
          status,
          specContent,
        });
        successPaths.push(path);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to upload API spec";
        failureEntries.push({ path, error: message });
      }

      setUploadedCount((c) => c + 1);
    }

    setSuccesses(successPaths);
    setFailures(failureEntries);
    setStep("summary");
  };

  const handleFolderSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    if (includeSubdirsRef.current === null) return;

    const allFiles = Array.from(fileList);
    const specFiles = filterSpecFiles(allFiles, includeSubdirsRef.current);

    if (specFiles.length === 0) {
      setScanError("No JSON or YAML spec files found in the selected folder.");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setScanError(null);
    await runUpload(specFiles);
  };

  const progressValue =
    totalCount > 0 ? Math.round((uploadedCount / totalCount) * 100) : 0;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={step === "uploading"}
    >
      <DialogTitle>
        {step === "confirmSubdirs" && "Bulk Upload API Specs"}
        {step === "uploading" && "Uploading API Specs"}
        {step === "summary" && "Upload Complete"}
      </DialogTitle>

      <DialogContent>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          {...({ webkitdirectory: "" } as React.InputHTMLAttributes<HTMLInputElement>)}
          accept=".json,.yaml,.yml,application/json"
          onChange={handleFolderSelected}
        />

        {step === "confirmSubdirs" && (
          <Box>
            <Typography sx={{ mb: 2 }}>
              Select a folder containing OpenAPI spec files (.json, .yaml, .yml).
              Each file will be uploaded as a separate API spec. The file name
              (without extension) will be used as the API name. All specs in the
              batch will share the version, status, and enabled settings below.
            </Typography>

            <Stack spacing={2} sx={{ mb: 3 }}>
              <TextField
                label="Version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                fullWidth
                required
                error={!versionValid}
                helperText={
                  !versionValid ? "Version is required" : undefined
                }
              />

              <TextField
                select
                label="Status"
                value={status}
                onChange={(e) =>
                  handleStatusChange(e.target.value as EntityStatus)
                }
                fullWidth
              >
                {Object.values(EntityStatus).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>

              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={(e) => handleEnabledChange(e.target.checked)}
                  />
                }
                label="Enabled"
              />
            </Stack>

            <Typography sx={{ mb: 2, fontWeight: 600 }}>
              Include files in subdirectories?
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Button
                variant={includeSubdirs === true ? "contained" : "outlined"}
                onClick={() => handleSubdirChoice(true)}
                disabled={!versionValid}
                sx={{ textTransform: "none" }}
              >
                Yes, include subdirectories
              </Button>
              <Button
                variant={includeSubdirs === false ? "contained" : "outlined"}
                onClick={() => handleSubdirChoice(false)}
                disabled={!versionValid}
                sx={{ textTransform: "none" }}
              >
                No, only this folder
              </Button>
            </Box>

            {scanError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {scanError}
              </Alert>
            )}
          </Box>
        )}

        {step === "uploading" && (
          <Box>
            <Typography sx={{ mb: 2 }}>
              Uploaded {uploadedCount} of {totalCount}
            </Typography>
            <LinearProgress variant="determinate" value={progressValue} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Processing files one at a time. Please do not close this dialog.
            </Typography>
          </Box>
        )}

        {step === "summary" && (
          <Box>
            <Typography sx={{ mb: 2 }}>
              <strong>Succeeded:</strong> {successes.length} &nbsp;|&nbsp;{" "}
              <strong>Failed:</strong> {failures.length}
            </Typography>

            {successes.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  Successful uploads ({successes.length})
                </Typography>
                <List dense disablePadding>
                  {successes.map((path) => (
                    <ListItem key={path} disableGutters sx={{ py: 0.25 }}>
                      <ListItemText
                        primary={path}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {failures.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Failed uploads ({failures.length})
                </Typography>
                <List dense disablePadding>
                  {failures.map(({ path, error }) => (
                    <ListItem key={path} disableGutters sx={{ py: 0.25 }}>
                      <ListItemText
                        primary={path}
                        secondary={error}
                        primaryTypographyProps={{ variant: "body2" }}
                        secondaryTypographyProps={{ variant: "caption" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === "confirmSubdirs" && (
          <Button onClick={handleDialogClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        )}

        {step === "uploading" && (
          <Button
            onClick={() => {
              abortRef.current = true;
            }}
            color="warning"
            sx={{ textTransform: "none" }}
          >
            Stop after current file
          </Button>
        )}

        {step === "summary" && (
          <Button
            variant="contained"
            onClick={handleDialogClose}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadApiSpecsDialog;
